<script setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import moment from 'moment'
import useStore from './../store'
import { isAndroid, isEnabledFlag, makeGoogleCalendarUrl, makeIcsFile } from './../helpers/index'

const emit = defineEmits(['move-time', 'cancel-item', 'cancel-booking'])

// Store
const store = useStore()
const { totalPeople, attractions, booking, options } = storeToRefs(store)

const totalPaidValueMinusDiscounts = computed(() => {
  let total = store?.booking?.totalPaid ?? 0;

  if (store?.booking?.payments && store?.booking.payments.length > 0){
    for (const payment of store?.booking?.payments) {
      if (['VOUCHER', 'PROMO_CODE'].includes(payment.type)){
        total -= payment;
      }
    }
  }

  return ` AUD $${(total).toFixed(2)}`
})

const totalPaidValue = computed(() => {
  let total = store?.booking?.totalPaid ?? 0;

  return ` AUD $${(total).toFixed(2)}`
})

// Methods
const humanTime = (time) => {
  return moment(time).format("dddd Do MMMM YYYY, h:mma")
}

const itemTotal = (price, qty) => {
  return (price * qty).toFixed(2)
}

const attractionFromProductCode = (productCode) => {
  return store.attractions.length > 0 ? store.attractions.find(a => a.rezdy_retail_product_code === productCode) : null;
}

const base = window.bookingWidget.base;

const downloadEvent = (item) => {
  if (isAndroid()) {
    const url = makeGoogleCalendarUrl({
      start: moment(item.startTimeLocal).toDate(),
      end: moment(item.endTimeLocal).toDate(),
    }, item.productName);
    window.open(url, '_blank');
  } else {
    makeIcsFile({
      start: moment(item.startTimeLocal).toDate(),
      end: moment(item.endTimeLocal).toDate(),
    }, item.productName);
  }
}

const itemIsInPast = (item) => {
  const startTime = item?.startTimeLocal ?? item?.startTime ?? null

  return startTime ? moment(startTime).isBefore(moment()) : false
}

const moveBookingCutoffMinutes = (item) => {
  const attraction = attractionFromProductCode(item?.productCode)
  return Number(attraction?.move_booking_cutoff_minutes ?? 0)
}

const moveBookingsEnabled = (item) => {
  const attraction = attractionFromProductCode(item?.productCode)
  return isEnabledFlag(attraction?.enabled_move_bookings)
}

const cancelBookingsEnabled = (item) => {
  const attraction = attractionFromProductCode(item?.productCode)
  return isEnabledFlag(attraction?.enabled_cancel_bookings)
}

const itemIsWithinMoveCutoff = (item) => {
  const startTime = item?.startTimeLocal ?? item?.startTime ?? null
  const cutoffMinutes = moveBookingCutoffMinutes(item)

  if (!startTime || cutoffMinutes <= 0) {
    return false
  }

  return moment(startTime).diff(moment(), "minutes", true) <= cutoffMinutes
}

const bookingHasPastItems = computed(() => {
  const items = booking.value?.items ?? []

  return items.some((item) => itemIsInPast(item))
})

const canMoveItem = (item) => {
  return !bookingHasPastItems.value && moveBookingsEnabled(item) && !!item?.productCode && !!attractionFromProductCode(item.productCode);

  // return !bookingHasPastItems.value && !itemIsWithinMoveCutoff(item) && !!item?.productCode && !!attractionFromProductCode(item.productCode)
}

const canCancelItem = (item) => {
  return cancelBookingsEnabled(item) && !itemIsInPast(item)
}

const canCancelBooking = computed(() => {
  if (booking.value.status !== 'CONFIRMED'){
    return false;
  }

  const items = booking.value?.items ?? []

  return items.length > 0 && items.every((item) => canCancelItem(item))
})

</script>

<template>
  <div>

    <div v-if="booking && booking.items && booking.items.length > 0" class="pb-4 md:py-4">
      <ul class="list-none flex flex-col gap-4">
        <li v-for="(item, itemInd) in booking.items"
            :key="item.id"
            class="bg-light rounded-lg relative px-4 py-6 mb-8">
          <span class="h-12 w-12 absolute left-4 -top-6 bg-primary text-light rounded-full inline-flex justify-center items-center leading-none">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.80598 10.0918L15.4459 2.2168C16.5115 1.60156 17.9042 1.97474 18.5194 3.04035L19.5917 4.89757C19.7675 5.20203 19.7126 5.5991 19.4913 5.84864C19.0359 6.39575 18.9565 7.17229 19.308 7.78122C19.6772 8.42059 20.3894 8.7401 21.0909 8.61924C21.4176 8.55238 21.789 8.70335 21.9647 9.00781L23.0194 10.8346C23.6522 11.9306 23.2615 13.2929 22.1959 13.9081L8.55598 21.7831C7.45992 22.416 6.11523 22.0556 5.48242 20.9596L4.42773 19.1328C4.25195 18.8283 4.27643 18.4489 4.52815 18.1817C4.95314 17.6522 5.06298 16.8581 4.69384 16.2187C4.34228 15.6098 3.59962 15.3079 2.92855 15.4111C2.57135 15.4956 2.23047 15.327 2.05469 15.0226L0.982422 13.1654C0.367188 12.0997 0.709916 10.7246 1.80598 10.0918ZM2.44384 12.3216L3.23486 13.6917C4.38618 13.7171 5.54002 14.3094 6.15526 15.375C6.78807 16.471 6.72407 17.7664 6.1704 18.7762L6.94384 20.1158C7.10204 20.3898 7.43821 20.4799 7.71223 20.3217L21.3521 12.4467C21.5957 12.3061 21.7162 11.9523 21.558 11.6783L20.7846 10.3387C19.6028 10.3309 18.4794 9.72103 17.8466 8.62497C17.2314 7.55935 17.2649 6.28153 17.849 5.25418L17.058 3.8841C16.9174 3.64053 16.5332 3.53759 16.2896 3.67821L2.64973 11.5532C2.37571 11.7114 2.30321 12.078 2.44384 12.3216ZM16.2547 11.4926L14.5672 8.5698L7.7472 12.5073L9.4347 15.4301L16.2547 11.4926ZM6.00454 12.8639C5.70571 12.3463 5.86828 11.6435 6.41631 11.3271L14.2105 6.82713C14.7281 6.5283 15.4485 6.72133 15.7473 7.23891L17.9973 11.136C18.3137 11.6841 18.1031 12.374 17.5855 12.6728L9.79131 17.1728C9.24328 17.4892 8.57094 17.3091 8.25454 16.761L6.00454 12.8639Z"
                    fill="currentColor"/>
            </svg>
          </span>
          <h4 class="text-xl font-bold">{{ attractionFromProductCode(item.productCode)?.retail_title ?? item.productName }}, {{ attractionFromProductCode(item.productCode)?.retail_location ?? '' }}</h4>
          <p class="text-dark">{{ humanTime(item.startTimeLocal) }}</p>
          <p class="mt-2 flex gap-2">
            <svg class="w-4 h-5 text-primary" width="16" height="19" viewBox="0 0 16 19" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M2.28571 1.72244L15.4643 6.54883C15.7857 6.653 16 6.9655 16 7.278C16 7.62522 15.7857 7.93772 15.4643 8.04188L2.28571 12.8336V17.278C2.28571 17.903 1.75 18.3891 1.14286 18.3891C0.5 18.3891 0 17.903 0 17.278V1.72244C0 1.13216 0.5 0.611328 1.14286 0.611328C1.75 0.611328 2.28571 1.13216 2.28571 1.72244Z"
                    fill="currentColor"/>
            </svg>
            {{ attractionFromProductCode(item.productCode)?.retail_meta ?? '' }}
          </p>

          <ul class="text-dark mb-0">
            <li
                v-for="(quantity, ind) in item.quantities"
                :key="`${item.id}-${ind}`"
                aria-label="price item"
                class="flex py-3 text-dark"
            >
              <div class="w-1/2">
                <div
                    :title="`$${quantity.optionPrice.toFixed(2)}`"
                >
                  {{ `${quantity.value}x ${quantity.optionLabel}` }}
                </div>
                <div class="text-sm text-grey">{{ attractionFromProductCode(item.productCode)?.retail_title ?? item.productName }}</div>
              </div>
              <div class="w-1/2 text-right">
                {{ `$${itemTotal(quantity.optionPrice, quantity.value)}` }}
              </div>
            </li>
            <li
                v-for="(extra, ind) in item.extras"
                :key="`${item.id}-${ind}-extra`"
                aria-label="price item"
                class="flex py-2 text-dark flex-wrap"
            >
              <div class="w-1/2">
                <div class="text-base text-dark">{{ `${extra.quantity}x ${extra.name}` }}</div>
              </div>
              <div class="w-1/2 text-right">
                {{ `$${itemTotal(extra.price, extra.quantity)}` }}
              </div>
            </li>
          </ul>

          <div class="flex flex-col gap-2">
            <button
               v-if="canMoveItem(item)"
               type="button"
               class="leading-none font-bold rounded-full border-2 border-dark bg-white hover:bg-light py-3 px-6 flex gap-2 items-center justify-center"
               @click.prevent="emit('move-time', item, itemInd)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
              <span>Move booking time</span>
            </button>
            <button
               v-if="canCancelItem(item)"
               type="button"
               class="leading-none font-bold rounded-full border-2 border-dark bg-white hover:bg-light py-3 px-6 flex gap-2 items-center justify-center"
               @click.prevent="emit('cancel-item', item, itemInd)"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 7.5h12m-10.5 0v10.125a1.125 1.125 0 0 0 1.125 1.125h6.75a1.125 1.125 0 0 0 1.125-1.125V7.5m-7.5 0V6a1.125 1.125 0 0 1 1.125-1.125h3.75A1.125 1.125 0 0 1 15 6v1.5" />
              </svg>
              <span>Cancel this item</span>
            </button>
            <a :href="`${base}/download/ticket/${booking.orderNumber}/${item.productCode}`"
               v-if="false"
               target="_blank"
               class="leading-none font-bold rounded-full border-2 border-dark bg-white hover:bg-light py-3 px-6 flex gap-2 items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.80598 10.0918L15.4459 2.2168C16.5115 1.60156 17.9042 1.97474 18.5194 3.04035L19.5917 4.89757C19.7675 5.20203 19.7126 5.5991 19.4913 5.84864C19.0359 6.39575 18.9565 7.17229 19.308 7.78122C19.6772 8.42059 20.3894 8.7401 21.0909 8.61924C21.4176 8.55238 21.789 8.70335 21.9647 9.00781L23.0194 10.8346C23.6522 11.9306 23.2615 13.2929 22.1959 13.9081L8.55598 21.7831C7.45992 22.416 6.11523 22.0556 5.48242 20.9596L4.42773 19.1328C4.25195 18.8283 4.27643 18.4489 4.52815 18.1817C4.95314 17.6522 5.06298 16.8581 4.69384 16.2187C4.34228 15.6098 3.59962 15.3079 2.92855 15.4111C2.57135 15.4956 2.23047 15.327 2.05469 15.0226L0.982422 13.1654C0.367188 12.0997 0.709916 10.7246 1.80598 10.0918ZM2.44384 12.3216L3.23486 13.6917C4.38618 13.7171 5.54002 14.3094 6.15526 15.375C6.78807 16.471 6.72407 17.7664 6.1704 18.7762L6.94384 20.1158C7.10204 20.3898 7.43821 20.4799 7.71223 20.3217L21.3521 12.4467C21.5957 12.3061 21.7162 11.9523 21.558 11.6783L20.7846 10.3387C19.6028 10.3309 18.4794 9.72103 17.8466 8.62497C17.2314 7.55935 17.2649 6.28153 17.849 5.25418L17.058 3.8841C16.9174 3.64053 16.5332 3.53759 16.2896 3.67821L2.64973 11.5532C2.37571 11.7114 2.30321 12.078 2.44384 12.3216ZM16.2547 11.4926L14.5672 8.5698L7.7472 12.5073L9.4347 15.4301L16.2547 11.4926ZM6.00454 12.8639C5.70571 12.3463 5.86828 11.6435 6.41631 11.3271L14.2105 6.82713C14.7281 6.5283 15.4485 6.72133 15.7473 7.23891L17.9973 11.136C18.3137 11.6841 18.1031 12.374 17.5855 12.6728L9.79131 17.1728C9.24328 17.4892 8.57094 17.3091 8.25454 16.761L6.00454 12.8639Z"
                      fill="currentColor"/>
              </svg>
              <span>Download tickets</span>
            </a>
            <a :href="`#`"
               @click.prevent="downloadEvent(item)"
               target="_blank"
               class="leading-none font-bold rounded-full border-2 border-dark bg-white hover:bg-light py-3 px-6 flex gap-2 items-center justify-center">
              <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10.0547 2.9375V4.5H15.6797V2.9375C15.6797 2.42969 16.0703 2 16.6172 2C17.125 2 17.5547 2.42969 17.5547 2.9375V4.5H19.1172C20.4844 4.5 21.6172 5.63281 21.6172 7V7.625V9.5V19.5C21.6172 20.9062 20.4844 22 19.1172 22H6.61719C5.21094 22 4.11719 20.9062 4.11719 19.5V9.5V7.625V7C4.11719 5.63281 5.21094 4.5 6.61719 4.5H8.17969V2.9375C8.17969 2.42969 8.57031 2 9.11719 2C9.625 2 10.0547 2.42969 10.0547 2.9375ZM5.99219 9.5V19.5C5.99219 19.8516 6.26562 20.125 6.61719 20.125H19.1172C19.4297 20.125 19.7422 19.8516 19.7422 19.5V9.5H5.99219Z" fill="currentColor"/>
              </svg>
              <span>Add to calendar</span>
            </a>

            <div class="grid grid-cols-2">
              <a :href="attractionFromProductCode(item.productCode)?.apple_map_link ?? ''"
                 target="_blank"
                 class="leading-none font-bold rounded-l-full border-2 border-r border-dark bg-white hover:bg-light py-3 px-6 flex gap-2 items-center justify-center">
                <svg width="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 170 170" height="18">
                  <path
                      fill="currentColor"
                      d="m150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.197-2.12-9.973-3.17-14.34-3.17-4.58 0-9.492 1.05-14.746 3.17-5.262 2.13-9.501 3.24-12.742 3.35-4.929 0.21-9.842-1.96-14.746-6.52-3.13-2.73-7.045-7.41-11.735-14.04-5.032-7.08-9.169-15.29-12.41-24.65-3.471-10.11-5.211-19.9-5.211-29.378 0-10.857 2.346-20.221 7.045-28.068 3.693-6.303 8.606-11.275 14.755-14.925s12.793-5.51 19.948-5.629c3.915 0 9.049 1.211 15.429 3.591 6.362 2.388 10.447 3.599 12.238 3.599 1.339 0 5.877-1.416 13.57-4.239 7.275-2.618 13.415-3.702 18.445-3.275 13.63 1.1 23.87 6.473 30.68 16.153-12.19 7.386-18.22 17.731-18.1 31.002 0.11 10.337 3.86 18.939 11.23 25.769 3.34 3.17 7.07 5.62 11.22 7.36-0.9 2.61-1.85 5.11-2.86 7.51zm-31.26-123.01c0 8.1021-2.96 15.667-8.86 22.669-7.12 8.324-15.732 13.134-25.071 12.375-0.119-0.972-0.188-1.995-0.188-3.07 0-7.778 3.386-16.102 9.399-22.908 3.002-3.446 6.82-6.3113 11.45-8.597 4.62-2.2516 8.99-3.4968 13.1-3.71 0.12 1.0831 0.17 2.1663 0.17 3.2409z"
                  />
                </svg>
                <span>Apple Maps</span>
              </a>
              <a :href="attractionFromProductCode(item.productCode)?.google_map_link ?? ''"
                 target="_blank"
                 class="leading-none font-bold rounded-r-full border-2 border-l border-dark  bg-white hover:bg-light py-3 px-6 flex gap-2 items-center justify-center">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    shape-rendering="geometricPrecision"
                    text-rendering="geometricPrecision"
                    image-rendering="optimizeQuality"
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    viewBox="0 0 640 640"
                >
                  <path
                      fill="currentColor"
                      d="M326.331 274.255v109.761h181.49c-7.37 47.115-54.886 138.002-181.49 138.002-109.242 0-198.369-90.485-198.369-202.006 0-111.509 89.127-201.995 198.369-201.995 62.127 0 103.761 26.516 127.525 49.359l86.883-83.635C484.99 31.512 412.741-.012 326.378-.012 149.494-.012 6.366 143.116 6.366 320c0 176.884 143.128 320.012 320.012 320.012 184.644 0 307.256-129.876 307.256-312.653 0-21-2.244-36.993-5.008-52.997l-302.248-.13-.047.024z"
                  />
                </svg>
                <span>Google Maps</span>
              </a>
            </div>
          </div>
        </li>
      </ul>
    </div>

    <div class="py-2" v-if="booking.payments && booking.payments.length > 0">
      <div class="mb-4 font-bold uppercase text-sm">Payments/Vouchers</div>
      <ul class="list-none flex flex-col gap-4">
        <li v-for="payment in booking.payments"
            :key="payment.label"
            class="flex relative">
          <div class="w-1/2">
            <div class="text-base text-dark">{{ payment.type === 'CREDITCARD' ? `Card payment` : `${payment.label}` }}</div>
            <div class="text-sm text-grey" v-if="payment.type === 'PROMO_CODE'">Promo code</div>
            <div class="text-sm text-grey" v-if="payment.type === 'VOUCHER'">Voucher</div>
          </div>
          <div class="w-1/2 text-right">
            {{ `AUD $${(payment?.amount ?? 0).toFixed(2)}` }}
          </div>
        </li>
      </ul>
    </div>
    <div
         class="flex justify-between items-center border-t border-b border-dotted border-primary py-2 mt-4">
      <div class="flex flex-col lg:flex-row gap-1">
        <strong>Total</strong>
        <span>(inc GST)</span>
      </div>
      <div class="text-xl">{{ totalPaidValue }}</div>
    </div>

    <div v-if="canCancelBooking" class="pt-6">
      <button
        type="button"
        class="inline-flex items-center justify-center rounded-full border-2 border-dark bg-white px-6 py-3 font-bold text-dark hover:bg-light"
        @click.prevent="emit('cancel-booking')"
      >
        Cancel entire booking
      </button>
    </div>
  </div>
</template>
